import ArrowRightIcon from "@/assets/icons/arrow-right.svg";
import ArrowUpIcon from "@/assets/icons/arrow-top.svg";
import IAIdeaIcon from "@/assets/images/ia-idea.png";
import loadingVistaAssistantAnimation from "@/assets/lotties/ecoute_logo.json";
import treatingVistaAssistantAnimation from "@/assets/lotties/logo_traite.json";
import AISuggestion from "@/domain/enums/AISuggestions";
import useMicrophone from "@/hooks/adapters/useMicrophone";
import useGetAISuggestions from "@/hooks/useGetAISuggestions";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import Lottie from "lottie-react";
import { FormEvent, useRef } from "react";
import { Button } from "../ui/button";
import Spinner from "../ui/spinner";
import { Textarea } from "../ui/textarea";
import MicrophoneButton from "./MicrophoneButton";

const MAX_INPUT_LENGTH = 1000;

interface InitialInputProps {
    className?: string;
    inputValue: string;
    setInputValue: (inputValue: string) => void;
    handleSubmitInput: (e: FormEvent<HTMLFormElement>) => void;
    isSendingMessage: boolean;
    suggestionType: AISuggestion;
    placeholder: string;
    showSuggestions?: boolean;
    isWaitingForDeepAnalysis?: boolean;
    isLoadingMoreData?: boolean;
}

const InitialInput = ({
    className,
    inputValue,
    setInputValue,
    handleSubmitInput,
    isSendingMessage,
    suggestionType,
    placeholder,
    showSuggestions = true,
    isWaitingForDeepAnalysis = false,
    isLoadingMoreData = false,
}: InitialInputProps) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const suggestions = useGetAISuggestions({
        suggestionType,
    });

    const {
        handleStartListening,
        handleStopListening,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable,
        isDictationDisabled,
        listening,
        resetTranscript,
    } = useMicrophone(setInputValue, MAX_INPUT_LENGTH);

    return (
        <div className={cn("flex w-full flex-col gap-4", className)}>
            <div className="relative rounded-md bg-surface-primary">
                <form
                    className="flex w-full"
                    onSubmit={(e) => {
                        handleStopListening();
                        resetTranscript();
                        handleSubmitInput(e);
                    }}
                >
                    <div className="flex-1">
                        <Lottie
                            animationData={
                                isSendingMessage ||
                                isWaitingForDeepAnalysis ||
                                isLoadingMoreData
                                    ? loadingVistaAssistantAnimation
                                    : treatingVistaAssistantAnimation
                            }
                            className="absolute top-[-4px] h-15 w-15 fill-primary-dark"
                            loop
                        />
                        <Textarea
                            ref={inputRef}
                            className="pl-15 pt-3.5"
                            disabled={
                                isSendingMessage || isWaitingForDeepAnalysis
                            }
                            onChange={(e) => {
                                if (e.target.value.length > MAX_INPUT_LENGTH)
                                    return;
                                setInputValue(e.target.value);
                                resetTranscript();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleStopListening();
                                    resetTranscript();
                                    const formEvent = new Event(
                                        "submit",
                                    ) as unknown as FormEvent<HTMLFormElement>;
                                    handleSubmitInput(formEvent);
                                }
                            }}
                            placeholder={placeholder}
                            rows={4}
                            value={inputValue}
                            variant="invisible"
                        />
                    </div>
                    <div className="flex flex-col items-end justify-between p-2 transition-opacity duration-300">
                        <div>
                            <MicrophoneButton
                                browserSupportsSpeechRecognition={
                                    browserSupportsSpeechRecognition
                                }
                                isDictationDisabled={isDictationDisabled}
                                isMicrophoneAvailable={isMicrophoneAvailable}
                                isMicrophoneTemporarilyDisabled={
                                    isSendingMessage || isWaitingForDeepAnalysis
                                }
                                listening={listening}
                                startListening={handleStartListening}
                                stopListening={handleStopListening}
                            />
                        </div>
                        <div>
                            <Button
                                className="mb-1 min-w-25 rounded-full p-2"
                                disabled={
                                    isSendingMessage ||
                                    isWaitingForDeepAnalysis ||
                                    isLoadingMoreData
                                }
                                type="submit"
                                variant="default"
                            >
                                {isSendingMessage ||
                                isWaitingForDeepAnalysis ||
                                isLoadingMoreData ? (
                                    <Spinner
                                        className="text-white"
                                        size="small"
                                    />
                                ) : (
                                    <span className="flex items-center gap-1 pl-2">
                                        {t("customViewDialog.vistaInput.send")}
                                        <img
                                            alt=""
                                            className="mt-[-3px]"
                                            src={ArrowUpIcon}
                                        />
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
            <div
                className={cn(
                    "flex h-60 flex-col items-start justify-between rounded-2xl bg-white/20 p-4 transition-all duration-1000",
                    !showSuggestions && "h-0 overflow-hidden p-0",
                )}
            >
                <img alt="Assistant Logo" src={IAIdeaIcon} />
                <div className="mt-2 flex flex-col gap-2">
                    {suggestions.slice(0, 4).map((proposal) => (
                        <Button
                            key={proposal}
                            className="flex w-fit cursor-pointer items-center gap-2 rounded-md bg-surface-primary px-4 py-2"
                            onClick={() => {
                                setInputValue(proposal);
                                inputRef.current?.focus();
                            }}
                            variant="secondary"
                        >
                            <img alt="" src={ArrowRightIcon} />
                            {proposal}...
                        </Button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default InitialInput;
